import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type UserRole = "customer" | "vendor" | "admin";
type VendorStatus = "pending" | "approved" | "rejected" | "suspended" | null;

interface VendorInfo {
  id: string;
  status: VendorStatus;
  business_name: string;
  stripe_account_id: string | null;
  stripe_onboarding_complete: boolean;
  tour_auto_shown: boolean;

}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  vendorLoading: boolean;
  roles: UserRole[];
  isVendor: boolean;
  isAdmin: boolean;
  vendorInfo: VendorInfo | null;
  signOut: () => Promise<void>;
  refreshVendorInfo: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [vendorLoading, setVendorLoading] = useState(true);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [vendorInfo, setVendorInfo] = useState<VendorInfo | null>(null);
  const hasLoadedVendorInfoRef = useRef(false);

  // Verify Stripe account status and sync with database
  const verifyStripeStatus = async (vendorId: string, stripeAccountId: string | null): Promise<boolean> => {
    if (!stripeAccountId) return false;
    
    try {
      const { data, error } = await supabase.functions.invoke("check-stripe-account-status", {
        body: { vendorId },
      });
      
      if (error) {
        console.error("Error verifying Stripe status:", error);
        return false;
      }
      
      return data?.stripe_onboarding_complete ?? false;
    } catch (error) {
      console.error("Error calling check-stripe-account-status:", error);
      return false;
    }
  };

  const fetchVendorInfo = async (userId: string) => {
    try {
      // First check if user is a vendor owner
      const { data, error } = await supabase
        .from("vendors")
        .select("id, status, business_name, stripe_account_id, stripe_onboarding_complete, tour_auto_shown")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        const vendorData = data;
        // If vendor has a Stripe account, ALWAYS re-verify its current status
        // so the DB self-heals in both directions (false→true and true→false)
        // without the vendor having to visit Settings. check-stripe-account-status
        // updates the vendors row as a side-effect, so a stale DB value gets
        // corrected on every app load.
        let stripeComplete = vendorData.stripe_onboarding_complete ?? false;

        if (vendorData.stripe_account_id) {
          // Throttle re-verification: at most once every 30 minutes per vendor.
          const throttleKey = `stripe-verify:${vendorData.id}`;
          const lastChecked = Number(localStorage.getItem(throttleKey) ?? 0);
          const THIRTY_MIN = 30 * 60 * 1000;
          const shouldVerify = Date.now() - lastChecked > THIRTY_MIN;

          if (shouldVerify) {
            try {
              const verified = await verifyStripeStatus(vendorData.id, vendorData.stripe_account_id);
              // Only trust the verified result; if the edge call errored it returns
              // false — in that case keep the cached DB value instead of flipping
              // the whole UI to "Connect Stripe".
              stripeComplete = verified || stripeComplete;
              localStorage.setItem(throttleKey, String(Date.now()));
            } catch {
              // Network/edge error — keep cached DB value, don't degrade UX.
            }
          }
        }
        
        setVendorInfo({
          id: vendorData.id,
          status: vendorData.status as VendorStatus,
          business_name: vendorData.business_name,
          stripe_account_id: vendorData.stripe_account_id,
          stripe_onboarding_complete: stripeComplete,
          tour_auto_shown: (vendorData as { tour_auto_shown?: boolean }).tour_auto_shown ?? true,
        });

        hasLoadedVendorInfoRef.current = true;
      } else {
        setVendorInfo(null);
        hasLoadedVendorInfoRef.current = true;
      }
    } catch (error) {
      console.error("Error fetching vendor info:", error);
      setVendorInfo(null);
      hasLoadedVendorInfoRef.current = true;
    } finally {
      setVendorLoading(false);
    }
  };

  const fetchUserRoles = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      if (error) throw error;
      setRoles((data || []).map((r) => r.role as UserRole));
    } catch (error) {
      console.error("Error fetching user roles:", error);
      setRoles([]);
    }
  };

  // Public method to refresh vendor info (for manual refresh after Stripe onboarding)
  const refreshVendorInfo = async () => {
    if (user) {
      setVendorLoading(true);
      await fetchVendorInfo(user.id);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let lastAuthUserId: string | null = null;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;

        const nextUser = session?.user ?? null;
        const nextUserId = nextUser?.id ?? null;
        const isSameAuthenticatedUser = !!nextUserId && nextUserId === lastAuthUserId;

        setSession(session);
        setUser(nextUser);

        // Skip refetch on background token refreshes — this fires when the
        // browser tab regains focus and would otherwise flip vendorLoading,
        // unmounting the vendor layout and flushing in-progress form state.
        if (event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
          lastAuthUserId = nextUserId;
          return;
        }

        // Fetch roles and vendor info for ongoing auth changes
        if (nextUser) {
          if (!isSameAuthenticatedUser || !hasLoadedVendorInfoRef.current) {
            setVendorLoading(true);
          }
          // Use setTimeout to avoid Supabase auth deadlock
          setTimeout(() => {
            if (isMounted) {
              fetchUserRoles(nextUser.id);
              fetchVendorInfo(nextUser.id);
            }
          }, 0);
        } else {
          setRoles([]);
          setVendorInfo(null);
          hasLoadedVendorInfoRef.current = false;
          setVendorLoading(false);
        }
        lastAuthUserId = nextUserId;
      }
    );

    // Initial auth load - controls loading state
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        lastAuthUserId = session?.user?.id ?? null;

        if (session?.user) {
          // Fetch roles and vendor info BEFORE setting loading to false
          await Promise.all([
            fetchUserRoles(session.user.id),
            fetchVendorInfo(session.user.id),
          ]);
        } else {
          setVendorLoading(false);
        }
      } catch (error) {
        console.error("Error during auth initialization:", error);
        setVendorLoading(false);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRoles([]);
    setVendorInfo(null);
    hasLoadedVendorInfoRef.current = false;
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    vendorLoading,
    roles,
    isVendor: roles.includes("vendor"),
    isAdmin: roles.includes("admin"),
    vendorInfo,
    signOut,
    refreshVendorInfo,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
